/**
 * TripAccessGuard - Protects trip routes by validating access before rendering
 * Single Responsibility: Only handles access control UI and routing
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Loader2, Clock, XCircle, MapPin, ArrowLeft, 
  Home, AlertTriangle, RefreshCw 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useTripAccess, ACCESS_STATUS } from '../../hooks/useTripAccess';
import { useLanguage } from '../../contexts/LanguageContext';

// Translations
const translations = {
  loading: {
    title: { 'pt-BR': 'Verificando acesso...', 'en-US': 'Checking access...' },
  },
  pending: {
    title: { 'pt-BR': 'Aguardando Aprovação', 'en-US': 'Awaiting Approval' },
    description: { 
      'pt-BR': 'Sua solicitação para participar desta viagem está sendo analisada pelo organizador.',
      'en-US': 'Your request to join this trip is being reviewed by the organizer.'
    },
    tip: {
      'pt-BR': 'Você receberá uma notificação assim que o organizador aprovar sua entrada.',
      'en-US': 'You will receive a notification as soon as the organizer approves your request.'
    },
  },
  rejected: {
    title: { 'pt-BR': 'Acesso Negado', 'en-US': 'Access Denied' },
    description: {
      'pt-BR': 'Infelizmente sua solicitação para participar desta viagem não foi aprovada.',
      'en-US': 'Unfortunately, your request to join this trip was not approved.'
    },
  },
  notMember: {
    title: { 'pt-BR': 'Você não é membro', 'en-US': 'You are not a member' },
    description: {
      'pt-BR': 'Você não faz parte desta viagem. Solicite acesso ao organizador.',
      'en-US': 'You are not part of this trip. Request access from the organizer.'
    },
  },
  notFound: {
    title: { 'pt-BR': 'Viagem não encontrada', 'en-US': 'Trip not found' },
    description: {
      'pt-BR': 'Esta viagem não existe ou foi removida.',
      'en-US': 'This trip does not exist or has been removed.'
    },
  },
  error: {
    title: { 'pt-BR': 'Erro ao carregar', 'en-US': 'Loading error' },
    description: {
      'pt-BR': 'Ocorreu um erro ao verificar seu acesso. Tente novamente.',
      'en-US': 'An error occurred while checking your access. Please try again.'
    },
  },
  buttons: {
    back: { 'pt-BR': 'Voltar', 'en-US': 'Back' },
    dashboard: { 'pt-BR': 'Ir para Dashboard', 'en-US': 'Go to Dashboard' },
    myTrips: { 'pt-BR': 'Minhas Viagens', 'en-US': 'My Trips' },
    retry: { 'pt-BR': 'Tentar novamente', 'en-US': 'Try again' },
  },
};

// Loading Screen
function LoadingScreen() {
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['pt-BR'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          {t(translations.loading.title)}
        </h2>
      </motion.div>
    </div>
  );
}

// Pending Approval Screen
function PendingScreen({ tripData, onBack }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['pt-BR'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.1 }}
              className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Clock className="w-10 h-10 text-amber-500" />
            </motion.div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t(translations.pending.title)}
            </h1>

            {/* Trip name */}
            {tripData && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  {tripData.name}
                </span>
              </div>
            )}

            {/* Description */}
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {t(translations.pending.description)}
            </p>

            {/* Tip */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                💡 {t(translations.pending.tip)}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/trips')}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {t(translations.buttons.myTrips)}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                {t(translations.buttons.dashboard)}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Rejected/Access Denied Screen
function RejectedScreen({ tripData }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['pt-BR'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.1 }}
              className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <XCircle className="w-10 h-10 text-red-500" />
            </motion.div>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t(translations.rejected.title)}
            </h1>

            {tripData && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span className="text-slate-500 dark:text-slate-400">
                  {tripData.name}
                </span>
              </div>
            )}

            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {t(translations.rejected.description)}
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/trips')}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {t(translations.buttons.myTrips)}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                {t(translations.buttons.dashboard)}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Not a Member Screen - Different from Rejected (user never requested to join)
function NotMemberScreen({ tripData }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['pt-BR'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.1 }}
              className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <MapPin className="w-10 h-10 text-slate-400" />
            </motion.div>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t(translations.notMember.title)}
            </h1>

            {tripData && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  {tripData.name}
                </span>
              </div>
            )}

            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {t(translations.notMember.description)}
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/trips')}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {t(translations.buttons.myTrips)}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                {t(translations.buttons.dashboard)}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Not Found Screen
function NotFoundScreen() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['pt-BR'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.1 }}
              className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <MapPin className="w-10 h-10 text-slate-400" />
            </motion.div>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t(translations.notFound.title)}
            </h1>

            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {t(translations.notFound.description)}
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/trips')}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {t(translations.buttons.myTrips)}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t(translations.buttons.back)}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Error Screen
function ErrorScreen({ error, onRetry }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['pt-BR'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.1 }}
              className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </motion.div>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t(translations.error.title)}
            </h1>

            <p className="text-slate-600 dark:text-slate-400 mb-2">
              {t(translations.error.description)}
            </p>

            {error && (
              <p className="text-sm text-red-500 mb-6">{error}</p>
            )}

            <div className="flex flex-col gap-3">
              <Button
                onClick={onRetry}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t(translations.buttons.retry)}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                {t(translations.buttons.dashboard)}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

/**
 * TripAccessGuard Component
 * Wraps trip pages and validates access before rendering children
 */
export function TripAccessGuard({ children }) {
  const { tripId } = useParams();
  const navigate = useNavigate();
  
  const { 
    status, 
    tripData, 
    error, 
    refetch 
  } = useTripAccess(tripId);

  // Render based on access status
  switch (status) {
    case ACCESS_STATUS.LOADING:
      return <LoadingScreen />;
    
    case ACCESS_STATUS.AUTHORIZED:
      return children;
    
    case ACCESS_STATUS.PENDING:
      return <PendingScreen tripData={tripData} onBack={() => navigate(-1)} />;
    
    case ACCESS_STATUS.REJECTED:
      return <RejectedScreen tripData={tripData} />;
    
    case ACCESS_STATUS.NOT_MEMBER:
      return <NotMemberScreen tripData={tripData} />;
    
    case ACCESS_STATUS.NOT_FOUND:
      return <NotFoundScreen />;
    
    case ACCESS_STATUS.ERROR:
    default:
      return <ErrorScreen error={error} onRetry={refetch} />;
  }
}

export default TripAccessGuard;
